package com.example.positionahead.ui.screens

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.material3.Button
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.ExposedDropdownMenuBox
import androidx.compose.material3.ExposedDropdownMenuDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.dp
import com.example.positionahead.domain.MarketSuggestionCatalog
import com.example.positionahead.domain.PositionSizerCalculator
import com.example.positionahead.model.AssetClass
import com.example.positionahead.model.DiscoverAssetClass
import com.example.positionahead.model.Quote
import com.example.positionahead.model.SavedPortfolio
import com.example.positionahead.util.formatCurrency
import com.example.positionahead.util.formatNumber

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PositionSizerSection(
    quotes: Map<String, Quote>,
    portfolios: List<SavedPortfolio>,
    activePortfolioId: String,
    initialSymbol: String?,
    initialDollarAmount: Double? = null,
    onLookupQuote: (String) -> Unit,
    onAddPurchase: (
        symbol: String,
        shares: Double,
        costBasis: Double,
        assetClass: AssetClass,
        purchaseDate: String?,
        note: String?,
        portfolioId: String?,
    ) -> Unit,
    onDone: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val prefill = initialSymbol?.trim()?.uppercase().orEmpty()
    val planDollars = initialDollarAmount?.takeIf { it > 0 }
    var symbol by remember(prefill) { mutableStateOf(prefill) }
    var totalInvest by remember { mutableStateOf("") }
    var positionPercent by remember { mutableStateOf("") }
    var selectedPortfolioId by remember(activePortfolioId) { mutableStateOf(activePortfolioId) }
    var portfolioExpanded by remember { mutableStateOf(false) }
    var assetClass by remember { mutableStateOf(AssetClass.STOCK) }
    var assetExpanded by remember { mutableStateOf(false) }

    LaunchedEffect(symbol) {
        if (symbol.length >= 1) {
            onLookupQuote(symbol)
            MarketSuggestionCatalog.entryFor(symbol)?.let { entry ->
                assetClass = entry.assetClass.toAssetClass()
            }
        }
    }

    val quote = quotes[symbol.uppercase()]
    val totalValue = totalInvest.toDoubleOrNull()
    val percentValue = positionPercent.toDoubleOrNull()
    val result = if (quote != null && totalValue != null && percentValue != null) {
        PositionSizerCalculator.calculate(totalValue, percentValue, quote.price)
    } else {
        null
    }
    val selectedPortfolio = portfolios.find { it.id == selectedPortfolioId }

    val planShares = if (quote != null && planDollars != null) planDollars / quote.price else null

    Column(modifier = modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Text("Position sizer", style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.SemiBold)
        Text(
            if (planDollars != null) {
                "From Plan: confirm this purchase, then return to Plan to refresh."
            } else {
                "Size a position, pick a portfolio, and add it in one step."
            },
            style = MaterialTheme.typography.bodySmall,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        )

        if (planDollars != null && symbol.isNotBlank()) {
            PlanTradeQuickAdd(
                symbol = symbol,
                dollarAmount = planDollars,
                quote = quote,
                shares = planShares,
                selectedPortfolio = selectedPortfolio,
                assetClass = assetClass,
                onAddPurchase = onAddPurchase,
                onDone = onDone,
                selectedPortfolioId = selectedPortfolioId,
            )
            HorizontalDivider(modifier = Modifier.padding(vertical = 4.dp))
        }

        ExposedDropdownMenuBox(
            expanded = portfolioExpanded,
            onExpandedChange = { portfolioExpanded = it },
        ) {
            OutlinedTextField(
                value = selectedPortfolio?.let { portfolioLabel(it) } ?: "Select portfolio",
                onValueChange = {},
                readOnly = true,
                label = { Text("Add to portfolio") },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(portfolioExpanded) },
                modifier = Modifier.fillMaxWidth().menuAnchor(),
            )
            ExposedDropdownMenu(
                expanded = portfolioExpanded,
                onDismissRequest = { portfolioExpanded = false },
            ) {
                portfolios.forEach { portfolio ->
                    DropdownMenuItem(
                        text = { Text(portfolioLabel(portfolio)) },
                        onClick = {
                            selectedPortfolioId = portfolio.id
                            portfolioExpanded = false
                        },
                    )
                }
            }
        }

        OutlinedTextField(
            value = symbol,
            onValueChange = { symbol = it.uppercase() },
            label = { Text("Symbol") },
            placeholder = { Text("QQQM") },
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )

        ExposedDropdownMenuBox(
            expanded = assetExpanded,
            onExpandedChange = { assetExpanded = it },
        ) {
            OutlinedTextField(
                value = assetClass.name,
                onValueChange = {},
                readOnly = true,
                label = { Text("Asset class") },
                trailingIcon = { ExposedDropdownMenuDefaults.TrailingIcon(assetExpanded) },
                modifier = Modifier.fillMaxWidth().menuAnchor(),
            )
            ExposedDropdownMenu(
                expanded = assetExpanded,
                onDismissRequest = { assetExpanded = false },
            ) {
                AssetClass.entries.forEach { option ->
                    DropdownMenuItem(
                        text = { Text(option.name) },
                        onClick = {
                            assetClass = option
                            assetExpanded = false
                        },
                    )
                }
            }
        }

        OutlinedTextField(
            value = totalInvest,
            onValueChange = { totalInvest = it.filter { c -> c.isDigit() || c == '.' } },
            label = { Text("Total to invest ($)") },
            placeholder = { Text("10000") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
        )

        OutlinedTextField(
            value = positionPercent,
            onValueChange = { positionPercent = it.filter { c -> c.isDigit() || c == '.' } },
            label = { Text("Position size (%)") },
            placeholder = { Text("25") },
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Decimal),
            modifier = Modifier.fillMaxWidth(),
            singleLine = true,
            supportingText = {
                if (percentValue != null && percentValue > 100) {
                    Text("Percent cannot exceed 100", color = MaterialTheme.colorScheme.error)
                }
            },
        )

        when {
            symbol.isBlank() -> Unit
            quote == null -> {
                Text(
                    "Fetching price for $symbol…",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                )
            }
            result != null -> {
                Card(
                    modifier = Modifier.fillMaxWidth(),
                    colors = CardDefaults.cardColors(
                        containerColor = MaterialTheme.colorScheme.primaryContainer.copy(alpha = 0.35f),
                    ),
                ) {
                    Column(
                        modifier = Modifier.padding(16.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        Text(
                            symbol.uppercase(),
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                        )
                        Text(
                            "At ${formatCurrency(quote.price)} per share",
                            style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.75f),
                        )
                        ResultRow("Buy this much", formatCurrency(result.dollarAmount))
                        ResultRow("Shares to buy", formatNumber(result.shares, decimals = 4))
                        Text(
                            "${formatNumber(percentValue ?: 0.0, 1)}% of ${formatCurrency(totalValue ?: 0.0)}",
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.primary,
                        )
                    }
                }

                Button(
                    onClick = {
                        onAddPurchase(
                            symbol.uppercase(),
                            result.shares,
                            result.pricePerShare,
                            assetClass,
                            null,
                            "Sized at ${formatNumber(percentValue ?: 0.0, 1)}% of ${formatCurrency(totalValue ?: 0.0)}",
                            selectedPortfolioId,
                        )
                        totalInvest = ""
                        positionPercent = ""
                        onDone()
                    },
                    modifier = Modifier.fillMaxWidth(),
                    enabled = percentValue != null && percentValue in 0.0..100.0,
                ) {
                    Text(
                        "Add to ${selectedPortfolio?.name ?: "portfolio"}",
                    )
                }
            }
            else -> {
                Text(
                    "Enter a valid total and percent (1–100) to see amounts.",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.6f),
                )
            }
        }
    }
}

private fun portfolioLabel(portfolio: SavedPortfolio): String =
    if (portfolio.isTheoretical) "${portfolio.name} (theoretical)" else portfolio.name

@Composable
private fun PlanTradeQuickAdd(
    symbol: String,
    dollarAmount: Double,
    quote: Quote?,
    shares: Double?,
    selectedPortfolio: SavedPortfolio?,
    assetClass: AssetClass,
    selectedPortfolioId: String,
    onAddPurchase: (
        symbol: String,
        shares: Double,
        costBasis: Double,
        assetClass: AssetClass,
        purchaseDate: String?,
        note: String?,
        portfolioId: String?,
    ) -> Unit,
    onDone: () -> Unit,
) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.secondaryContainer.copy(alpha = 0.5f),
        ),
    ) {
        Column(modifier = Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Text("Planned buy", fontWeight = FontWeight.Bold, style = MaterialTheme.typography.titleSmall)
            Text(
                "Buy ${formatCurrency(dollarAmount)} of ${symbol.uppercase()}",
                style = MaterialTheme.typography.bodyMedium,
            )
            when {
                quote == null -> Text("Loading price…", style = MaterialTheme.typography.bodySmall)
                shares != null -> {
                    Text(
                        "≈ ${formatNumber(shares, 4)} shares at ${formatCurrency(quote.price)}",
                        style = MaterialTheme.typography.bodySmall,
                    )
                    Button(
                        onClick = {
                            onAddPurchase(
                                symbol.uppercase(),
                                shares,
                                quote.price,
                                assetClass,
                                null,
                                "From Plan tab (${formatCurrency(dollarAmount)})",
                                selectedPortfolioId,
                            )
                            onDone()
                        },
                        modifier = Modifier.fillMaxWidth(),
                    ) {
                        Text("Add to ${selectedPortfolio?.name ?: "portfolio"}")
                    }
                }
            }
        }
    }
}

private fun DiscoverAssetClass.toAssetClass(): AssetClass = when (this) {
    DiscoverAssetClass.STOCK -> AssetClass.STOCK
    DiscoverAssetClass.ETF -> AssetClass.ETF
    DiscoverAssetClass.BOND -> AssetClass.BOND
    DiscoverAssetClass.MUTUAL_FUND -> AssetClass.ETF
}

@Composable
private fun ResultRow(label: String, value: String) {
    Column(modifier = Modifier.fillMaxWidth()) {
        Text(
            label,
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onSurface.copy(alpha = 0.7f),
        )
        Text(
            value,
            style = MaterialTheme.typography.headlineSmall,
            fontWeight = FontWeight.Bold,
        )
    }
}
